/** Daily vendor board. Values are served net fields; no HUD arithmetic on legs. */
import { lastSessionFreshness, type LastSessionFreshness, type ServedVenueClock } from "./order33State.ts";
export type GexMetric = "gex" | "delta" | "charm" | "vanna";
/** The toggle's order and its words; a label is a name, never the key upper-cased (DELTA reads DEX). */
export const GEX_METRIC_LABELS = [["gex", "GEX"], ["delta", "DEX"], ["charm", "CHARM"], ["vanna", "VANNA"]] as const;
export interface GridColumn {
  expiry: string; header: string; state: "OK" | "INCOMPLETE" | "CAPTURE_REFUSED";
  hatched: boolean; boardDate: string | null; checkedTs: string | null; detail: string | null;
}
export interface GridCell {
  expiry: string; strike: number; value: number | null; fill: "up" | "dn" | null;
  opacity: number; absent: boolean;
}
export interface GridModel {
  ok: true; columns: GridColumn[]; strikes: number[]; cells: GridCell[]; max: number;
  rootPill: string; boardPill: string; prior: boolean; stale: boolean; ageS: number | null;
  /** The pill's word: the worker's stale beside the door's own venue (ORDER_HUD_GEX_GRID_CLOSED_1 D1 (a)). */
  freshness: LastSessionFreshness;
}
export interface GridRefusal { ok: false; refusal: string }
const record = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === "object" && !Array.isArray(v);
const finite = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v);
const date = (v: unknown): v is string => typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v);
const refuse = (refusal: string): GridRefusal => ({ ok: false, refusal });
const fields = { gex: "net_gex", delta: "net_delta", charm: "net_charm", vanna: "net_vanna" } as const;
const venueClock = (body: Record<string, unknown>): ServedVenueClock | null => record(body.venue_state) ? body.venue_state : null;

export function gexGridModel(body: unknown, metric: GexMetric, httpStatus = 200): GridModel | GridRefusal {
  if (record(body) && body.ok === false && typeof body.refused === "string") return refuse(body.refused);
  if (httpStatus === 400) return refuse("GRID UNSERVED");
  if (httpStatus < 200 || httpStatus >= 300) return refuse(`HTTP_${httpStatus}${record(body) && typeof body.error === "string" ? ` · ${body.error}` : ""}`);
  if (!record(body)) return refuse("PAYLOAD_INVALID");
  if (body.ok === false) return refuse(typeof body.error === "string" ? body.error : "PAYLOAD_INVALID");
  if (body.root_scope !== "VENDOR_SPX_TICKER_POOLS_SPXW") return refuse("ROOT_SCOPE_UNKNOWN");
  if (body.ok !== true || body.source !== "typed" || body.root !== "SPX" || body.hypothesis !== true
    || !Array.isArray(body.columns) || !body.columns.length || !Array.isArray(body.rows)
    || !(metric in fields) || (body.board_date !== null && !date(body.board_date))
    || !["CURRENT", "PRIOR"].includes(String(body.board_session)) || typeof body.stale !== "boolean"
    || !(body.age_s === null || (finite(body.age_s) && body.age_s >= 0))) return refuse("PAYLOAD_INVALID");
  const columns: GridColumn[] = [];
  for (const c of body.columns) {
    if (!record(c) || !date(c.expiry) || (c.state === "OK" ? !finite(c.dte) : c.dte !== null && !finite(c.dte))
      || !["OK", "INCOMPLETE", "CAPTURE_REFUSED"].includes(String(c.state))
      || !(c.board_date === null || date(c.board_date))
      || !(c.checked_ts === null || (typeof c.checked_ts === "string" && Number.isFinite(Date.parse(c.checked_ts))))
      || !(c.detail === null || typeof c.detail === "string")
      || columns.some(col => col.expiry === c.expiry)) return refuse("PAYLOAD_INVALID");
    const state = c.state as GridColumn["state"];
    const hatched = state !== "OK";
    const header = hatched ? state : `${c.expiry.slice(5)} ${c.dte}D`;
    columns.push({ expiry: c.expiry, state, hatched,
      header: body.board_date === null ? `${header} · ${c.board_date === null ? "UNKNOWN" : c.board_date.slice(5)}` : header,
      boardDate: c.board_date, checkedTs: c.checked_ts as string | null, detail: c.detail as string | null });
  }
  const values = new Map<string, Map<number, number>>();
  const strikeSet = new Set<number>();
  let max = 0;
  for (const r of body.rows) {
    if (!record(r) || typeof r.expiry !== "string" || !finite(r.strike) || !finite(r[fields[metric]])) return refuse("PAYLOAD_INVALID");
    const column = columns.find(c => c.expiry === r.expiry);
    if (!column) return refuse("PAYLOAD_INVALID");
    if (column.hatched) continue;
    const value = r[fields[metric]] as number;
    const rows = values.get(r.expiry) ?? new Map<number, number>();
    if (rows.has(r.strike)) return refuse("PAYLOAD_INVALID");
    rows.set(r.strike, value);
    values.set(r.expiry, rows);
    strikeSet.add(r.strike);
    max = Math.max(max, Math.abs(value));
  }
  const strikes = [...strikeSet].sort((a, b) => a - b);
  const cells: GridCell[] = [];
  for (const column of columns) {
    if (column.hatched) continue;
    for (const strike of strikes) {
      const value = values.get(column.expiry)?.get(strike) ?? null;
      cells.push({ expiry: column.expiry, strike, value, absent: value === null,
        fill: value === null || value === 0 ? null : value > 0 ? "up" : "dn",
        opacity: value === null || value === 0 ? 0 : Math.max(0.12, Math.abs(value) / max) });
    }
  }
  const prior = body.board_session === "PRIOR";
  const boardPill = (body.board_date === null ? "BOARD MIXED" : `BOARD ${body.board_date.slice(5)}`) + (prior ? " · PRIOR" : "");
  return { ok: true, columns, strikes, cells, max, rootPill: "SPX+SPXW · pooled", boardPill,
    prior, stale: body.stale, ageS: body.age_s as number | null,
    freshness: lastSessionFreshness(body.stale, venueClock(body), [body.board_date as string | null]) };
}

/** ORDER_HUD_UW_FACE_1_BOX2 Part C: the door's 7-day per-strike sum (window=7d, spx 872edf61), served nets only. */
export interface SumCell { strike: number; value: number; fill: "up" | "dn" | null; opacity: number; expiriesN: number }
export interface Sum7dModel {
  ok: true; cells: SumCell[]; max: number; complete: boolean; header: string; expiries: string[];
  excluded: { expiry: string; state: string }[]; gaps: string[]; beyondWindow: string[]; boardDates: string[];
  /** The sum's OWN response age (it is fetched apart from the grid), never the grid's chip. */
  stale: boolean; ageS: number | null;
  /** The sum's own boards beside its own venue clock: CLOSED only when every board is the last session's. */
  freshness: LastSessionFreshness;
}
const dates = (v: unknown): v is string[] => Array.isArray(v) && v.every(date);

export function sum7dModel(body: unknown, metric: GexMetric, httpStatus = 200): Sum7dModel | GridRefusal {
  if (record(body) && body.ok === false && typeof body.refused === "string") return refuse(body.refused);
  if (httpStatus === 400) return refuse("SUM UNSERVED");
  if (httpStatus < 200 || httpStatus >= 300) return refuse(`HTTP_${httpStatus}${record(body) && typeof body.error === "string" ? ` · ${body.error}` : ""}`);
  // Fetched apart from the grid, so the sum proves its own identity (SPX is not SPXW; codex review r2 P2 2).
  if (record(body) && body.ok === true && body.root_scope !== "VENDOR_SPX_TICKER_POOLS_SPXW") return refuse("ROOT_SCOPE_UNKNOWN");
  if (!record(body) || body.ok !== true || body.source !== "typed" || body.root !== "SPX" || body.hypothesis !== true
    || !(metric in fields) || typeof body.stale !== "boolean"
    || !(body.age_s === null || (finite(body.age_s) && body.age_s >= 0))) return refuse("PAYLOAD_INVALID");
  const s = body.sum_7d;
  if (!record(s) || s.window !== "7d" || typeof s.complete !== "boolean" || !Array.isArray(s.rows) || !dates(s.expiries)
    || !dates(s.gaps) || !dates(s.beyond_window) || !dates(s.board_dates) || !Array.isArray(s.excluded)
    || !s.excluded.every(e => record(e) && date(e.expiry) && typeof e.state === "string")) return refuse("PAYLOAD_INVALID");
  const values = new Map<number, { value: number; expiriesN: number }>();
  let sumMax = 0;
  for (const r of s.rows) {
    if (!record(r) || !finite(r.strike) || !finite(r[fields[metric]]) || !finite(r.expiries_n) || values.has(r.strike)) return refuse("PAYLOAD_INVALID");
    const net = r[fields[metric]] as number;
    values.set(r.strike, { value: net, expiriesN: r.expiries_n });
    sumMax = Math.max(sumMax, Math.abs(net));
  }
  const cells: SumCell[] = [...values].sort(([a], [b]) => a - b).map(([strike, { value, expiriesN }]) => ({ strike, value, expiriesN,
    fill: value === 0 ? null : value > 0 ? "up" : "dn", opacity: value === 0 ? 0 : Math.max(0.12, Math.abs(value) / sumMax) }));
  return { ok: true, cells, max: sumMax, complete: s.complete, header: s.complete ? `${s.expiries.length} EXP` : "PARTIAL",
    expiries: s.expiries, excluded: s.excluded as { expiry: string; state: string }[], gaps: s.gaps,
    beyondWindow: s.beyond_window, boardDates: s.board_dates, stale: body.stale, ageS: body.age_s as number | null,
    freshness: lastSessionFreshness(body.stale, venueClock(body), s.board_dates) };
}
